import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import yogBg from '../../images/yg1.jpg';
import ConfirmDialog from '../../Components/ConfirmDialog';
import {
    FaCalendarAlt,
    FaClock,
    FaUserTie,
    FaCheckCircle,
    FaTimesCircle,
    FaHourglassHalf,
    FaExchangeAlt,
    FaTrash,
    FaVideo,
    FaMoneyBillWave,
    FaHistory,
    FaExclamationTriangle
} from 'react-icons/fa';

function MyAppointments() {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [isPWA, setIsPWA] = useState(false);
    const [appointmentToRemove, setAppointmentToRemove] = useState(null);

    useEffect(() => {
        const checkPWA = () => {
            const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                window.navigator.standalone === true;
            setIsPWA(standalone);
        };
        checkPWA();
        const mediaQuery = window.matchMedia('(display-mode: standalone)');
        const handleChange = () => checkPWA();
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Payment specific state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [appointmentToPay, setAppointmentToPay] = useState(null);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('Credit/Debit Card');
    const fetchAppointments = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }

            const response = await HttpInterceptor.get(`/api/appointments?${params.toString()}`);
            if (response.status === 200) {
                setAppointments(response.data.data || []);
                setFilteredAppointments(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching appointments:', error);
            toast.error('Failed to load appointments');
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);

    const handleRequestPayment = (appointment) => {
        setAppointmentToPay(appointment);
        setShowPaymentModal(true);
    };

    const handlePaymentComplete = async () => {
        if (!appointmentToPay) return;
        setIsProcessingPayment(true);
        try {
            const response = await HttpInterceptor.post(`/api/appointments/${appointmentToPay._id}/pay`, {
                paymentMethod: selectedPaymentMethod
            });
            if (response.status === 200) {
                toast.success('Payment processed successfully. Appointment confirmed!');
                fetchAppointments();
                setShowPaymentModal(false);
                setAppointmentToPay(null);
            }
        } catch (error) {
            console.error('Error processing payment:', error);
            toast.error(error.response?.data?.message || 'Failed to process payment');
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const handleAcceptCounterOffer = async (appointmentId) => {
        try {
            const response = await HttpInterceptor.post(`/api/appointments/${appointmentId}/accept-counter-offer`);
            if (response.status === 200) {
                toast.success('Alternative slot accepted successfully');
                fetchAppointments();
            }
        } catch (error) {
            console.error('Error accepting counter-offer:', error);
            toast.error(error.response?.data?.message || 'Failed to accept counter-offer');
        }
    };

    const handleRejectCounterOffer = async (appointmentId) => {
        try {
            const reason = prompt('Reason for rejection (optional):');
            if (reason === null) return; // Cancelled

            const response = await HttpInterceptor.post(`/api/appointments/${appointmentId}/reject-counter-offer`, {
                reason: reason || 'Member rejected alternative slot'
            });
            if (response.status === 200) {
                toast.success('Alternative slot rejected');
                fetchAppointments();
            }
        } catch (error) {
            console.error('Error rejecting counter-offer:', error);
            toast.error(error.response?.data?.message || 'Failed to reject counter-offer');
        }
    };

    const handleRemoveAppointment = async (appointmentId) => {
        try {
            const response = await HttpInterceptor.delete(`/api/appointments/${appointmentId}/hide`);
            if (response.status === 200) {
                toast.success('Appointment removed');
                fetchAppointments();
            }
        } catch (error) {
            console.error('Error removing appointment:', error);
            toast.error(error.response?.data?.message || 'Failed to remove appointment');
        } finally {
            setAppointmentToRemove(null);
        }
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return { date: 'N/A', time: 'N/A' };
        const date = new Date(dateTime);
        return {
            date: date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }),
            time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
        };
    };

    const getStatusBadge = (status, dateTime) => {
        const isPast = dateTime && new Date(dateTime) < new Date();
        const statusConfig = {
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: FaHourglassHalf, label: 'Pending Approval' },
            counter_offered: { color: 'bg-orange-100 text-orange-800', icon: FaExchangeAlt, label: 'Action Required (Alternative Slot)' },
            approved: { color: 'bg-blue-100 text-blue-800', icon: FaCheckCircle, label: 'Approved (Pending Payment)' },
            rejected: { color: 'bg-red-100 text-red-800', icon: FaTimesCircle, label: 'Rejected / Expired' },
            confirmed: { color: 'bg-green-100 text-green-800', icon: FaCheckCircle, label: 'Confirmed' },
            cancelled: { color: 'bg-gray-100 text-gray-800', icon: FaTimesCircle, label: 'Cancelled' },
            completed: { color: 'bg-purple-100 text-purple-800', icon: FaCheckCircle, label: 'Completed' }
        };

        let config = statusConfig[status] || statusConfig.pending;

        if (isPast && (status === 'confirmed' || status === 'approved')) {
            config = { color: 'bg-gray-200 text-gray-700', icon: FaHistory, label: 'Expired' };
        }

        const Icon = config.icon;

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                <Icon className="mr-1" />
                {config.label}
            </span>
        );
    };

    // Separate appointments into sections based on status and time
    const now = new Date();
    const upcomingAppointments = filteredAppointments.filter(apt => 
        (['confirmed', 'approved'].includes(apt.status)) && 
        new Date(apt.requestedDateTime) >= now
    );
    const pendingAppointments = filteredAppointments.filter(apt => 
        apt.status === 'pending' && 
        new Date(apt.requestedDateTime) >= now
    );
    const actionRequiredAppointments = filteredAppointments.filter(apt => 
        apt.status === 'counter_offered' && 
        new Date(apt.counterOfferDateTime || apt.requestedDateTime) >= now
    );
    const historyAppointments = filteredAppointments.filter(apt => 
        !['pending', 'counter_offered', 'approved', 'confirmed'].includes(apt.status) ||
        (new Date(apt.counterOfferDateTime || apt.requestedDateTime) < now)
    );

    return (
        <div className={`min-h-screen ${isPWA ? 'bg-gradient-to-b from-teal-50/40 via-blue-50/30 to-white' : 'bg-gray-50'}`}>
            {/* Hero Section */}
            <div className={`relative ${isPWA ? 'bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50' : 'bg-gradient-to-r from-blue-600 to-purple-700'} ${isPWA ? 'text-gray-900' : 'text-white'}`}>
                {isPWA && (
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-blue-400 to-indigo-400"></div>
                )}
                {!isPWA && (
                    <div className="absolute inset-0 bg-black opacity-20"></div>
                )}
                <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-8 sm:py-10 pb-12 sm:pb-14' : 'py-12'}`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl'} font-bold ${isPWA ? 'text-teal-800' : ''} ${isPWA ? 'mb-3' : 'mb-2'}`}>
                                My Appointments
                            </h1>
                            <p className={`${isPWA ? 'text-sm sm:text-base text-teal-600' : 'text-xl opacity-90'} mb-4 md:mb-0`}>
                                Track and manage your scheduled consultant appointments
                            </p>
                            {!isPWA && (
                                <button
                                    onClick={() => navigate('/student/services')}
                                    className="mt-6 px-5 py-2.5 bg-white text-blue-700 text-sm font-semibold rounded-lg shadow-sm hover:shadow-md hover:bg-gray-50 transition-all border border-blue-100"
                                >
                                    Find More Consultants
                                </button>
                            )}
                        </div>
                        {!isPWA && (
                            <div className="hidden md:block">
                                <img src={yogBg} alt="Yoga" className="w-32 h-32 rounded-full object-cover shadow-lg border-4 border-white/20" />
                            </div>
                        )}
                        {isPWA && (
                            <button
                                onClick={() => navigate('/student/services')}
                                className="px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-xs sm:text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                            >
                                Find More
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Overview */}
            <section className={`${isPWA ? 'py-6 sm:py-8 pb-10 sm:pb-12' : 'py-8'} ${isPWA ? 'bg-gradient-to-b from-teal-50/40 via-blue-50/30 to-white' : 'bg-gray-50'} relative`}>
                {isPWA && (
                <>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-teal-200/40 to-transparent"></div>
                </>
                )}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className={`grid grid-cols-2 md:grid-cols-4 ${isPWA ? 'gap-3 sm:gap-4 mb-6' : 'gap-6 mb-8'}`}>
                        {/* Total Appointments */}
                        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-teal-50/20 border-2 border-teal-200/50 shadow-xl overflow-hidden' : 'bg-blue-50'} rounded-xl ${isPWA ? 'p-4 sm:p-5 relative transition-all duration-300' : 'p-6'}`}>
                            {isPWA && (
                                <>
                                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-teal-300/60 to-transparent"></div>
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-teal-100/20 to-blue-100/10 rounded-full blur-2xl"></div>
                                </>
                            )}
                            <div className={`${isPWA ? 'relative z-10' : 'text-center'}`}>
                                {isPWA ? (
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[11px] sm:text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Total</p>
                                            <p className="text-2xl sm:text-3xl font-bold text-teal-800 leading-tight drop-shadow-sm">{appointments.length}</p>
                                        </div>
                                        <div className="bg-gradient-to-br from-teal-50 to-blue-50 text-teal-600 p-3 sm:p-3.5 rounded-xl shadow-md ring-1 ring-teal-100/50 flex-shrink-0 ml-2">
                                            <FaCalendarAlt className="text-lg sm:text-xl" />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-2xl font-bold text-blue-600 mb-2">{appointments.length}</div>
                                        <div className="text-sm text-blue-600">Total Appointments</div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Upcoming */}
                        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-emerald-50/20 border-2 border-emerald-200/50 shadow-xl overflow-hidden' : 'bg-green-50'} rounded-xl ${isPWA ? 'p-4 sm:p-5 relative transition-all duration-300' : 'p-6'}`}>
                            {isPWA && (
                                <>
                                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-300/60 to-transparent"></div>
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-100/20 to-green-100/10 rounded-full blur-2xl"></div>
                                </>
                            )}
                            <div className={`${isPWA ? 'relative z-10' : 'text-center'}`}>
                                {isPWA ? (
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[11px] sm:text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Upcoming</p>
                                            <p className="text-2xl sm:text-3xl font-bold text-emerald-800 leading-tight drop-shadow-sm">{upcomingAppointments.length}</p>
                                        </div>
                                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 text-emerald-600 p-3 sm:p-3.5 rounded-xl shadow-md ring-1 ring-emerald-100/50 flex-shrink-0 ml-2">
                                            <FaCheckCircle className="text-lg sm:text-xl" />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-2xl font-bold text-green-600 mb-2">{upcomingAppointments.length}</div>
                                        <div className="text-sm text-green-600">Upcoming</div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Pending */}
                        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-blue-50/20 border-2 border-blue-200/50 shadow-xl overflow-hidden' : 'bg-yellow-50'} rounded-xl ${isPWA ? 'p-4 sm:p-5 relative transition-all duration-300' : 'p-6'}`}>
                            {isPWA && (
                                <>
                                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-300/60 to-transparent"></div>
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100/20 to-indigo-100/10 rounded-full blur-2xl"></div>
                                </>
                            )}
                            <div className={`${isPWA ? 'relative z-10' : 'text-center'}`}>
                                {isPWA ? (
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[11px] sm:text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Pending</p>
                                            <p className="text-2xl sm:text-3xl font-bold text-blue-800 leading-tight drop-shadow-sm">{pendingAppointments.length}</p>
                                        </div>
                                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 p-3 sm:p-3.5 rounded-xl shadow-md ring-1 ring-blue-100/50 flex-shrink-0 ml-2">
                                            <FaHourglassHalf className="text-lg sm:text-xl" />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-2xl font-bold text-yellow-600 mb-2">{pendingAppointments.length}</div>
                                        <div className="text-sm text-yellow-600">Pending Approval</div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Action Required */}
                        <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 border-2 border-orange-200/50 shadow-xl overflow-hidden' : 'bg-orange-50'} rounded-xl ${isPWA ? 'p-4 sm:p-5 relative transition-all duration-300' : 'p-6'}`}>
                            {isPWA && (
                                <>
                                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/60 to-transparent"></div>
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-100/20 to-red-100/10 rounded-full blur-2xl"></div>
                                </>
                            )}
                            <div className={`${isPWA ? 'relative z-10' : 'text-center'}`}>
                                {isPWA ? (
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[11px] sm:text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide">Action Reqd</p>
                                            <p className="text-2xl sm:text-3xl font-bold text-orange-800 leading-tight drop-shadow-sm">{actionRequiredAppointments.length}</p>
                                        </div>
                                        <div className="bg-gradient-to-br from-orange-50 to-red-50 text-orange-600 p-3 sm:p-3.5 rounded-xl shadow-md ring-1 ring-orange-100/50 flex-shrink-0 ml-2">
                                            <FaExchangeAlt className="text-lg sm:text-xl" />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-2xl font-bold text-orange-600 mb-2">{actionRequiredAppointments.length}</div>
                                        <div className="text-sm text-orange-600">Action Required</div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className={`${isPWA ? 'bg-white/95 backdrop-blur-sm shadow-xl border-2 border-teal-100/50 p-5 rounded-xl' : 'bg-white rounded-lg shadow p-4'} mb-8 relative z-10 w-full md:w-fit`}>
                        <div className="flex flex-wrap gap-4 items-end">
                            <div className="flex-1 min-w-[200px]">
                                <label className={`block text-sm ${isPWA ? 'font-semibold text-teal-900 mb-1.5 uppercase tracking-wide' : 'font-medium text-gray-700 mb-1'}`}>
                                    Status
                                </label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className={`w-full ${isPWA ? 'px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-0 focus:border-teal-400 text-gray-700 bg-gray-50 transition-all' : 'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary'}`}
                                >
                                    <option value="all">All Appointments</option>
                                    <option value="pending">Pending Approval</option>
                                    <option value="counter_offered">Action Required</option>
                                    <option value="approved">Approved (Unpaid)</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="completed">Completed</option>
                                    <option value="rejected">Rejected / Expired</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>
                            {statusFilter !== 'all' && (
                                <button
                                    onClick={() => setStatusFilter('all')}
                                    className={`${isPWA ? 'px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg' : 'px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md'} text-sm font-medium text-gray-600 transition-colors`}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-6 sm:py-8' : 'py-8'}`}>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        <p className="mt-2 text-gray-600">Loading your appointments...</p>
                    </div>
                ) : filteredAppointments.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-lg shadow">
                        <FaCalendarAlt className="mx-auto h-16 w-16 text-gray-300" />
                        <h3 className="mt-4 text-xl font-medium text-gray-900">No appointments found</h3>
                        <p className="mt-2 text-gray-600">
                            {statusFilter !== 'all'
                                ? 'No appointments match the selected filter.'
                                : 'You have not booked any appointments yet.'}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* ACTION REQUIRED: Alternative Slots */}
                        {actionRequiredAppointments.length > 0 && (
                            <div>
                                <h2 className="text-xl font-bold text-orange-800 mb-5 flex items-center bg-orange-50 px-4 py-2 rounded-lg w-fit border border-orange-100 shadow-sm mt-8">
                                    <FaExchangeAlt className="mr-2.5 text-orange-600" />
                                    Action Required <span className="ml-2 bg-orange-600 text-white rounded-full px-2.5 py-0.5 text-xs">{actionRequiredAppointments.length}</span>
                                </h2>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {actionRequiredAppointments.map((appointment) => {
                                        const originalDate = formatDateTime(appointment.requestedDateTime);
                                        const counterDate = formatDateTime(appointment.counterOfferDateTime);

                                        return (
                                            <div key={appointment._id} className="bg-white rounded-xl shadow-md border border-orange-200 overflow-hidden flex flex-col relative transition-shadow hover:shadow-lg">
                                                <div className="absolute top-0 left-0 w-1.5 h-full bg-orange-500"></div>
                                                <div className="p-5 border-b border-gray-100 flex items-start justify-between bg-gradient-to-r from-orange-50 to-white">
                                                    <div className="flex-1 pr-3 pl-1">
                                                        <h3 className="text-lg font-bold text-gray-900 leading-tight mb-1">
                                                            {appointment.program?.name || 'Program'}
                                                        </h3>
                                                        <div className="flex items-center text-sm text-gray-600">
                                                            <FaUserTie className="mr-1.5 text-orange-400" />
                                                            <span
                                                                className="cursor-pointer hover:text-orange-600 font-medium transition-colors"
                                                                onClick={() => navigate(`/student/trainer/${appointment.consultant?._id}`)}
                                                            >
                                                                {appointment.consultant?.user?.name || appointment.consultant?.name || 'Consultant'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        {getStatusBadge(appointment.status, appointment.counterOfferDateTime || appointment.requestedDateTime)}
                                                    </div>
                                                </div>

                                                <div className="p-5 flex-1 flex flex-col gap-4">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div className="p-3.5 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
                                                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2.5">Your Original Request</p>
                                                            <div className="space-y-1.5 text-sm text-gray-700">
                                                                <div className="flex items-center"><FaCalendarAlt className="mr-2 text-gray-400" /> {originalDate.date}</div>
                                                                <div className="flex items-center"><FaClock className="mr-2 text-gray-400" /> {originalDate.time} <span className="text-gray-500 ml-1">({appointment.slotDuration})</span></div>
                                                            </div>
                                                        </div>

                                                        <div className="p-3.5 bg-orange-50 rounded-lg border border-orange-200 shadow-sm relative overflow-hidden">
                                                            <div className="absolute -right-4 -top-4 text-orange-200 opacity-20">
                                                                <FaExchangeAlt size={64} />
                                                            </div>
                                                            <p className="text-xs font-bold text-orange-800 uppercase tracking-wider mb-2.5 relative z-10">Alternative Recommended</p>
                                                            <div className="space-y-1.5 text-sm text-orange-900 relative z-10 font-medium">
                                                                <div className="flex items-center"><FaCalendarAlt className="mr-2 text-orange-400" /> {counterDate.date}</div>
                                                                <div className="flex items-center"><FaClock className="mr-2 text-orange-400" /> {counterDate.time} <span className="text-orange-600/70 ml-1">({appointment.counterOfferSlotDuration})</span></div>
                                                                <div className="flex items-center mt-2 pt-2 border-t border-orange-200/60"><span className="text-xs text-orange-700 font-bold uppercase mr-2">Fee</span> ₹{appointment.feeAmount}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {appointment.counterOfferNotes && (
                                                        <div className="text-sm italic bg-orange-50/50 p-3 rounded-lg border border-orange-100 text-orange-800 flex">
                                                            <FaExclamationTriangle className="mr-2 mt-0.5 text-orange-400 flex-shrink-0" />
                                                            <span>"{appointment.counterOfferNotes}"</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="p-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row gap-3 mt-auto">
                                                    <button
                                                        onClick={() => handleAcceptCounterOffer(appointment._id)}
                                                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-green-600 transition shadow-sm flex justify-center items-center"
                                                    >
                                                        Accept Date
                                                    </button>
                                                    <button
                                                        onClick={() => handleRejectCounterOffer(appointment._id)}
                                                        className="flex-1 px-4 py-2.5 bg-white border border-red-200 text-red-600 font-semibold rounded-lg hover:bg-red-50 transition shadow-sm flex justify-center items-center"
                                                    >
                                                        Decline
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* UPCOMING / APPROVED */}
                        {upcomingAppointments.length > 0 && (
                            <div>
                                <h2 className="text-xl font-bold text-blue-800 mb-5 flex items-center bg-blue-50 px-4 py-2 rounded-lg w-fit border border-blue-100 shadow-sm">
                                    <FaCalendarAlt className="mr-2.5 text-blue-600" />
                                    Upcoming Appointments <span className="ml-2 bg-blue-600 text-white rounded-full px-2.5 py-0.5 text-xs">{upcomingAppointments.length}</span>
                                </h2>
                                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                                    {upcomingAppointments.map((appointment) => {
                                        const { date, time } = formatDateTime(appointment.requestedDateTime);

                                        return (
                                            <div key={appointment._id} className="bg-gradient-to-br from-white via-blue-50/20 to-white shadow-md border border-gray-100 hover:border-blue-300 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col">
                                                <div className="p-5 border-b border-gray-100 flex items-start justify-between bg-white relative">
                                                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                                                    <div className="flex-1 pr-3">
                                                        <h3 className="text-lg font-bold text-gray-900 leading-tight mb-1 truncate">
                                                            {appointment.program?.name || 'Program'}
                                                        </h3>
                                                        <div className="flex items-center text-sm text-gray-500">
                                                            <FaUserTie className="mr-1.5 text-blue-400" />
                                                            <span
                                                                className="cursor-pointer hover:text-blue-600 font-medium transition-colors"
                                                                onClick={() => navigate(`/student/trainer/${appointment.consultant?._id}`)}
                                                            >
                                                                {appointment.consultant?.user?.name || appointment.consultant?.name || 'Consultant'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        {getStatusBadge(appointment.status, appointment.requestedDateTime)}
                                                    </div>
                                                </div>

                                                <div className="p-5 flex-1 flex flex-col bg-gray-50/30">
                                                    <div className="space-y-3 mb-5 flex-1">
                                                        <div className="flex items-center text-gray-700">
                                                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 text-blue-600 flex-shrink-0">
                                                                <FaCalendarAlt />
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-500 font-medium uppercase">Date & Time</p>
                                                                <p className="font-semibold">{date}</p>
                                                                <p className="text-sm">{time} ({appointment.slotDuration})</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center text-gray-700">
                                                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-3 text-green-600 flex-shrink-0">
                                                                <span className="font-bold">₹</span>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-500 font-medium uppercase">Total Fee</p>
                                                                <p className="font-semibold">₹{appointment.feeAmount} <span className="text-xs font-normal opacity-70">({appointment.paymentStatus})</span></p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="pt-4 border-t border-gray-100 mt-auto">
                                                        {appointment.status === 'confirmed' && appointment.paymentStatus === 'paid' && appointment.meetingLink && (
                                                            <a
                                                                href={appointment.meetingLink}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="w-full flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition shadow-md hover:shadow-lg"
                                                            >
                                                                <FaVideo className="mr-2" /> Join Meeting
                                                            </a>
                                                        )}
                                                        {appointment.status === 'approved' && appointment.paymentStatus === 'pending' && (
                                                            <button
                                                                onClick={() => handleRequestPayment(appointment)}
                                                                className="w-full flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-teal-600 transition shadow-md hover:shadow-lg"
                                                            >
                                                                <FaMoneyBillWave className="mr-2" /> Pay to Confirm
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* PENDING APPROVAL */}
                        {pendingAppointments.length > 0 && (
                            <div>
                                <h2 className="text-xl font-bold text-yellow-800 mb-5 flex items-center bg-yellow-50 px-4 py-2 rounded-lg w-fit border border-yellow-200 shadow-sm mt-8">
                                    <FaHourglassHalf className="mr-2.5 text-yellow-600 animate-pulse" />
                                    Awaiting Consultant Approval <span className="ml-2 bg-yellow-500 text-white rounded-full px-2.5 py-0.5 text-xs">{pendingAppointments.length}</span>
                                </h2>
                                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                                    {pendingAppointments.map((appointment) => {
                                        const { date, time } = formatDateTime(appointment.requestedDateTime);
                                        return (
                                            <div key={appointment._id} className="bg-white rounded-xl shadow border border-yellow-200 hover:shadow-md transition-shadow flex flex-col relative overflow-hidden">
                                                <div className="absolute top-0 left-0 w-1 h-full bg-yellow-400"></div>
                                                <div className="p-5 border-b border-gray-100 flex items-start justify-between bg-yellow-50/30">
                                                    <div className="flex-1 pr-3 pl-1">
                                                        <h3 className="text-lg font-bold text-gray-800 leading-tight mb-1 truncate">
                                                            {appointment.program?.name || 'Program'}
                                                        </h3>
                                                        <div className="text-sm font-medium text-gray-600 flex items-center">
                                                            <FaUserTie className="mr-1.5 text-yellow-600" />
                                                            {appointment.consultant?.user?.name || appointment.consultant?.name || 'Consultant'}
                                                        </div>
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        {getStatusBadge(appointment.status, appointment.requestedDateTime)}
                                                    </div>
                                                </div>
                                                <div className="p-5 flex-1 flex flex-col bg-gray-50/50">
                                                    <div className="grid grid-cols-2 gap-y-4 gap-x-2 text-sm">
                                                        <div>
                                                            <p className="text-xs text-gray-500 font-medium uppercase mb-0.5">Date</p>
                                                            <p className="font-semibold text-gray-800 flex items-center"><FaCalendarAlt className="mr-1.5 text-gray-400" size={12}/>{date}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500 font-medium uppercase mb-0.5">Time</p>
                                                            <p className="font-semibold text-gray-800 flex items-center"><FaClock className="mr-1.5 text-gray-400" size={12}/>{time}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500 font-medium uppercase mb-0.5">Duration</p>
                                                            <p className="font-semibold text-gray-700">{appointment.slotDuration}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500 font-medium uppercase mb-0.5">Fee</p>
                                                            <p className="font-bold text-gray-900">₹{appointment.feeAmount}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* HISTORY (Rejected, Completed, Cancelled) */}
                        {historyAppointments.length > 0 && (
                            <div>
                                <h2 className="text-xl font-bold text-gray-700 mb-5 flex items-center bg-gray-200 px-4 py-2 rounded-lg w-fit border border-gray-300 shadow-sm mt-8">
                                    <FaHistory className="mr-2.5 text-gray-600" />
                                    Past & History <span className="ml-2 bg-gray-500 text-white rounded-full px-2.5 py-0.5 text-xs">{historyAppointments.length}</span>
                                </h2>
                                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                                    {historyAppointments.map((appointment) => {
                                        const { date, time } = formatDateTime(appointment.requestedDateTime);
                                        return (
                                            <div key={appointment._id} className="bg-white rounded-xl shadow border border-gray-200 hover:shadow-md transition-shadow flex flex-col relative overflow-hidden">
                                                <div className="absolute top-0 left-0 w-1 h-full bg-gray-300"></div>
                                                <div className="p-5 border-b border-gray-100 flex items-start justify-between bg-gray-50">
                                                    <div className="flex-1 pr-3">
                                                        <h3 className="text-base font-bold text-gray-800 leading-tight mb-1 truncate">
                                                            {appointment.program?.name || 'Program'}
                                                        </h3>
                                                        <div className="text-sm font-medium text-gray-600 flex items-center">
                                                            <FaUserTie className="mr-1.5 text-gray-400" />
                                                            {appointment.consultant?.user?.name || appointment.consultant?.name || 'Consultant'}
                                                        </div>
                                                    </div>
                                                    <div className="flex-shrink-0 flex items-center gap-2">
                                                        {getStatusBadge(appointment.status, appointment.requestedDateTime)}
                                                        <button
                                                            onClick={() => setAppointmentToRemove(appointment._id)}
                                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                                            title="Remove from history"
                                                        >
                                                            <FaTrash size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="p-5 flex-1 flex flex-col">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <div className="flex flex-col">
                                                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Scheduled</span>
                                                            <span className="text-sm font-medium text-gray-700">{date} at {time}</span>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Fee</span>
                                                            <span className="text-sm font-bold text-gray-800 block">₹{appointment.feeAmount}</span>
                                                        </div>
                                                    </div>
                                                    {appointment.reason && appointment.status === 'rejected' && (
                                                        <div className="mt-auto pt-3 border-t border-red-100">
                                                            <p className="text-xs text-red-700 bg-red-50 p-2.5 rounded-lg border border-red-100 italic">
                                                                <span className="font-bold flex items-center mb-1"><FaExclamationTriangle className="mr-1.5" /> Feedback:</span>
                                                                {appointment.reason}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Payment Modal */}
            {showPaymentModal && appointmentToPay && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-900">Complete Payment</h3>
                            <button
                                onClick={() => setShowPaymentModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition"
                            >
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            <div className="bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start">
                                <FaExclamationTriangle className="mt-0.5 mr-3 flex-shrink-0" />
                                <div className="text-sm">
                                    <p className="font-semibold mb-1">Payment Required</p>
                                    <p>Please complete the payment of <strong>₹{appointmentToPay.feeAmount}</strong> to confirm your appointment with <strong>{appointmentToPay.consultant?.user?.name || appointmentToPay.consultant?.name || 'Consultant'}</strong>.</p>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-medium text-gray-900 mb-2">Select Payment Method</h4>
                                <div className="space-y-2">
                                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                                        <input
                                            type="radio"
                                            name="paymentMethod"
                                            value="Credit/Debit Card"
                                            checked={selectedPaymentMethod === 'Credit/Debit Card'}
                                            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                                        />
                                        <span className="ml-3 block text-sm font-medium text-gray-700">Credit/Debit Card</span>
                                    </label>
                                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                                        <input
                                            type="radio"
                                            name="paymentMethod"
                                            value="UPI"
                                            checked={selectedPaymentMethod === 'UPI'}
                                            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                                        />
                                        <span className="ml-3 block text-sm font-medium text-gray-700">UPI</span>
                                    </label>
                                    <label className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                                        <input
                                            type="radio"
                                            name="paymentMethod"
                                            value="Net Banking"
                                            checked={selectedPaymentMethod === 'Net Banking'}
                                            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                                        />
                                        <span className="ml-3 block text-sm font-medium text-gray-700">Net Banking</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-2">
                                <button
                                    onClick={() => setShowPaymentModal(false)}
                                    disabled={isProcessingPayment}
                                    className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handlePaymentComplete}
                                    disabled={isProcessingPayment}
                                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center min-w-[120px] transition disabled:opacity-70 disabled:cursor-not-allowed shadow-sm"
                                >
                                    {isProcessingPayment ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Processing...
                                        </>
                                    ) : (
                                        `Pay ₹${appointmentToPay.feeAmount}`
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            <ConfirmDialog
                isOpen={!!appointmentToRemove}
                onClose={() => setAppointmentToRemove(null)}
                onConfirm={() => handleRemoveAppointment(appointmentToRemove)}
                title="Remove Appointment"
                message="Are you sure you want to remove this appointment from your history? This action cannot be undone."
                confirmText="Remove"
                cancelText="Cancel"
                variant="danger"
            />
        </div>
    );
}

export default MyAppointments;
